/**
 * Kept run outputs: the logic both execution logs share (the console's
 * RunOutput and the next twin's KeptOutputs).
 *
 * Free text, JSON and arrays stay on the host that ran the mission
 * (ADR-20261009 Part 6.1, agion/Agion#2911). The server sends a description in
 * their place, and a reference when the host keeps the value. Nothing here
 * decides about that: a description with the server's reference can be opened
 * by asking the server to open it on its host; one without cannot. It renders
 * nothing.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';
import type { ApiFailure } from '../../../api-failure';

export type KeptOutputLabelKey =
  | 'keptOutput'
  | 'keptOutputUnavailable'
  | 'keptOutputNotKept'
  | 'keptOutputTooLarge'
  | 'keptOutputUnreachable'
  | 'openKeptOutput'
  | 'openKeptOutputNamed';

/** What the server needs to open a kept output: the run and the reference. */
export interface KeptOutputOpenRequest {
  missionId: string;
  executionId: string;
  hostId: string;
  artifactId: string;
}

/**
 * Resolves with the opened content. Rejects with the server's ApiFailure when
 * it refused the open: 404 (reference_unavailable) when the output cannot be
 * opened, 413 (reference_too_large) when it is too large to open here. Any
 * other rejection means the host that keeps it could not be reached.
 */
export type OpenKeptOutput = (request: KeptOutputOpenRequest) => Promise<string>;

/**
 * The approved texts, for a host that passes no translation.
 * openKeptOutputNamed is the Open button's accessible name; {name} is the output's name.
 */
export const KEPT_OUTPUT_DEFAULTS: Record<KeptOutputLabelKey, string> = {
  keptOutput: 'This output is kept on the host that ran the mission.',
  keptOutputUnavailable: 'This output is no longer available.',
  keptOutputNotKept: 'This output was not kept.',
  keptOutputTooLarge: 'This output is too large to open here.',
  keptOutputUnreachable: 'Could not reach the host that keeps this output. Try again.',
  openKeptOutput: 'Open',
  openKeptOutputNamed: 'Open {name}',
};

/** The text, from labels or the defaults, with {name} filled in. */
export function keptOutputLabel(
  labels: Partial<Record<KeptOutputLabelKey, string>>,
  key: KeptOutputLabelKey,
  name = '',
): string {
  return (labels[key] ?? KEPT_OUTPUT_DEFAULTS[key]).replace('{name}', name);
}

interface KeptOutputReference {
  executionId: string;
  hostId: string;
  artifactId: string;
}

/** One output the server described instead of sending it. */
export interface DescribedOutput {
  name: string;
  /** The description's own type: JSON and arrays open as JSON. */
  isJson: boolean;
  /** The holding host could not store the value (reason output_not_kept). */
  notKept?: boolean;
  reference?: KeptOutputReference;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/** The server's reference, read from the wire fields of a description. */
function referenceOf(description: Record<string, unknown>): KeptOutputReference | undefined {
  const ref = asRecord(description['ref']);
  const executionId = ref?.['execution_id'];
  const hostId = ref?.['host_id'];
  const artifactId = ref?.['artifact_id'];
  if (typeof executionId !== 'string' || typeof hostId !== 'string' || typeof artifactId !== 'string') return undefined;
  return { executionId, hostId, artifactId };
}

/**
 * Splits an output map into the values the server sent and the descriptions it
 * sent in place of the others. Anything other than a map is all values.
 */
export function splitRunOutput(output: unknown): { values: unknown; described: DescribedOutput[] } {
  const record = asRecord(output);
  if (!record) return { values: output, described: [] };
  const values: Record<string, unknown> = {};
  const described: DescribedOutput[] = [];
  for (const [name, value] of Object.entries(record)) {
    const description = asRecord(value);
    if (description?.['kind'] !== 'description') {
      values[name] = value;
      continue;
    }
    const type = description['type'];
    const reference = referenceOf(description);
    described.push({
      name,
      isJson: type === 'json' || type === 'array',
      ...(description['reason'] === 'output_not_kept' ? { notKept: true } : {}),
      ...(reference ? { reference } : {}),
    });
  }
  return { values: described.length > 0 ? values : output, described };
}

/** Whether the split left any value to show beside the descriptions. */
export function hasRunOutputValues(split: { values: unknown; described: DescribedOutput[] }): boolean {
  return split.described.length === 0 || Object.keys(split.values as Record<string, unknown>).length > 0;
}

export type KeptOutputState =
  | { phase: 'kept' }
  | { phase: 'opening' }
  | { phase: 'opened'; content: string }
  | { phase: 'notKept' }
  | { phase: 'unavailable' }
  | { phase: 'tooLarge' }
  | { phase: 'unreachable' };

/** The state's message, or undefined once the content is open. */
export function keptOutputMessage(
  state: KeptOutputState,
  labels: Partial<Record<KeptOutputLabelKey, string>>,
): string | undefined {
  switch (state.phase) {
    case 'opened':
      return undefined;
    case 'notKept':
      return keptOutputLabel(labels, 'keptOutputNotKept');
    case 'unavailable':
      return keptOutputLabel(labels, 'keptOutputUnavailable');
    case 'tooLarge':
      return keptOutputLabel(labels, 'keptOutputTooLarge');
    case 'unreachable':
      return keptOutputLabel(labels, 'keptOutputUnreachable');
    default:
      return keptOutputLabel(labels, 'keptOutput');
  }
}

function initialState(output: DescribedOutput): KeptOutputState {
  if (output.reference) return { phase: 'kept' };
  return output.notKept ? { phase: 'notKept' } : { phase: 'unavailable' };
}

/**
 * The HTTP status of a refused open, read from the adapter's ApiFailure fields.
 * It reads the fields rather than testing instanceof: the published package
 * builds @agion/ui/api-failure and this module into separate bundles, each with
 * its own ApiFailure class, so the app's failure is never an instance of the
 * class this bundle holds.
 */
function refusedStatus(error: unknown): number | undefined {
  if (error === null || typeof error !== 'object') return undefined;
  const { kind, status } = error as Partial<Pick<ApiFailure, 'kind' | 'status'>>;
  return kind === 'http' && typeof status === 'number' ? status : undefined;
}

function refusedState(error: unknown): KeptOutputState {
  const status = refusedStatus(error);
  if (status === 404) return { phase: 'unavailable' };
  if (status === 413) return { phase: 'tooLarge' };
  return { phase: 'unreachable' };
}

/**
 * One kept output's open: its state, whether it can be opened (the server's
 * reference, the run's mission and an opener are all present), the open, and
 * the ref of the opened content, which takes focus when an open succeeds. A
 * refused or failed open says why and can be tried again.
 */
export function useKeptOutputOpen(
  output: DescribedOutput,
  missionId: string | undefined,
  onOpen: OpenKeptOutput | undefined,
): { state: KeptOutputState; canOpen: boolean; open: () => void; openedRef: RefObject<HTMLDivElement | null> } {
  const [state, setState] = useState<KeptOutputState>(() => initialState(output));
  const alive = useRef(true);
  const openedRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    if (state.phase === 'opened') openedRef.current?.focus();
  }, [state.phase]);

  const reference = output.reference;
  const canOpen = reference !== undefined && missionId !== undefined && missionId !== '' && onOpen !== undefined;
  const open = () => {
    if (!canOpen) return;
    setState({ phase: 'opening' });
    onOpen({ missionId, ...reference }).then(
      (content) => {
        if (alive.current) setState({ phase: 'opened', content });
      },
      (error: unknown) => {
        if (alive.current) setState(refusedState(error));
      },
    );
  };
  return { state, canOpen, open, openedRef };
}
